import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import Scoreboard from '../components/Scoreboard';
import { CtaBand } from '../components/Primitives';
import { landing } from '../content/copy';
import './Landing.css';

/**
 * Landing page (spec §6.1).
 *
 * Mobile-first, and intentionally free of any Firebase import — this page must
 * open instantly from a WhatsApp link (Lighthouse mobile ≥90). That budget is
 * why the scoreboard's flip is CSS keyframes rather than a timer, why the
 * wordmark is a styled div rather than an image, and why nothing here is lazy:
 * there is nothing heavy enough to be worth a second request.
 *
 * No prize figures. The four categories in the money section carry the argument
 * without publishing an amount that moves with the entrant count (spec §5); the
 * figures live on /prizes behind the email/password gate. The entry fee is the
 * exception and is deliberate — it is a fixed, knowable cost people need before
 * they fill in a form.
 */
export default function Landing() {
  return (
    <Layout>
      <div className="stripes">
        <section className="hero">
          <h1 className="hero__wordmark">
            {landing.wordmark.map((line) => (
              <span key={line} className="hero__line">
                {line}
              </span>
            ))}
          </h1>
          <p className="hero__tagline">{landing.tagline}</p>

          <p className="hero__credentials">
            {landing.credentials.map((item, i) => (
              <span key={item} className="hero__credential">
                {i > 0 && <span className="hero__tick" aria-hidden="true" />}
                {item}
              </span>
            ))}
          </p>

          <Link to="/join" className="btn-hero hero__cta">
            <span className="btn-hero__label">{landing.ctaPrimary}</span>
            <span className="btn-hero__sub">{landing.ctaSub}</span>
          </Link>
        </section>
      </div>

      <Scoreboard
        label={landing.scoreboard.label}
        meta={landing.scoreboard.meta}
        stats={landing.scoreboard.stats}
      />

      <div className="stripes">
        <section className="wrap wrap--wide pitch-copy">
          <h2>{landing.pitch.heading}</h2>
          <p className="pitch-copy__body">{landing.pitch.body}</p>
        </section>

        <section className="wrap wrap--wide section">
          <h3>{landing.field.heading}</h3>
          <ul className="field-grid grid-hairline">
            {landing.field.items.map((item, i) => (
              <li key={item} className="field-grid__item">
                <span className="field-grid__num" aria-hidden="true">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="field-grid__text">{item}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="invert section money">
        <div className="wrap wrap--wide money__inner">
          <div>
            <p className="eyebrow">{landing.manyWays.eyebrow}</p>
            <h2 className="money__heading">{landing.manyWays.heading}</h2>
            <p className="money__body">{landing.manyWays.body}</p>
            <Link to="/prizes" className="btn money__cta">
              {landing.manyWays.cta}
            </Link>
          </div>

          <ul className="money__grid">
            {landing.manyWays.categories.map((cat) => (
              <li key={cat.title} className="money__cell">
                <span className="money__cell-title">{cat.title}</span>
                <span className="money__cell-detail">{cat.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <div className="stripes">
        <section className="wrap wrap--wide section">
          <p className="eyebrow eyebrow--rule">
            <span>{landing.howItWorks.heading}</span>
          </p>
          <ol className="steps">
            {landing.howItWorks.steps.map((step, i) => (
              <li key={step.title} className="steps__item">
                <span className="steps__num">Step {['one', 'two', 'three', 'four'][i]}</span>
                <h3 className="steps__title">{step.title}</h3>
                <p className="steps__detail">{step.detail}</p>
              </li>
            ))}
          </ol>
        </section>

        {/*
          High Rollers isn't in the design mock, but it's a real second league
          people ask about and the prize structure for it is still undefined
          ("ask the referee"). A short honest block beats an omission.
        */}
        <section className="wrap wrap--wide section high-rollers">
          <h3>{landing.highRollers.heading}</h3>
          <p className="high-rollers__body">{landing.highRollers.body}</p>
        </section>

        <CtaBand />
      </div>
    </Layout>
  );
}
